import type { PictureStyleRead } from '../../types/api';
import { groupPictureStyles } from './pictureStyles';

/** The chip value for "AI decides". Never sent: it becomes `picture_style: null`. */
const AI_DECIDES = '';

const AI_DECIDES_HINT = 'Picks one from your brand profile';

interface PictureStyleFieldProps {
  styles: readonly PictureStyleRead[];
  /** A registry key, or null for "AI decides". */
  selected: string | null;
  onSelect: (key: string | null) => void;
  disabled: boolean;
}

/**
 * "Picture style": the medium the AI pictures are made in.
 *
 * One radio group, so arrow keys move the choice across every family. "AI
 * decides" comes first and is the default; the styles follow in registry order
 * under their family. The hint under the legend reads the chosen style's blurb,
 * and each chip carries its own blurb as a tooltip.
 */
export function PictureStyleField({
  styles,
  selected,
  onSelect,
  disabled,
}: PictureStyleFieldProps) {
  const families = groupPictureStyles(styles);
  const chosen = styles.find((style) => style.key === selected) ?? null;
  const current = chosen?.key ?? AI_DECIDES;

  function renderChip(value: string, label: string, title: string) {
    const isSelected = current === value;
    return (
      <label
        className={isSelected ? 'chip chip--selected' : 'chip'}
        key={value || 'ai'}
        title={title}
      >
        <input
          className="chip__input"
          type="radio"
          name="picture-style"
          value={value}
          checked={isSelected}
          onChange={() => onSelect(value === AI_DECIDES ? null : value)}
        />
        <span>{label}</span>
      </label>
    );
  }

  return (
    <fieldset className="chip-group picture-style-field" disabled={disabled}>
      <legend className="field__label">Picture style</legend>
      <p className="field__hint">{chosen ? chosen.blurb : AI_DECIDES_HINT}</p>

      <div className="chip-group__options">
        {renderChip(AI_DECIDES, 'AI decides', AI_DECIDES_HINT)}
      </div>
      {families.map((family) => (
        <div className="picture-style-field__family" key={family.key}>
          <span className="picture-style-field__family-label">{family.label}</span>
          <div className="picture-style-field__chips">
            {family.styles.map((style) => renderChip(style.key, style.label, style.blurb))}
          </div>
        </div>
      ))}
    </fieldset>
  );
}
